import { useQuery } from "@tanstack/react-query";

import { api } from "@utils/api";

import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { Pagination } from "@customTypes/index";
import { Profile } from "@customTypes/profile";

import { profileQKeys } from "./queryKeys";


interface QueryProps {
  pagination?: Partial<Pagination>;
  search?: string;
  type?: "doctor" | "lab" | string;
  visibility?: "public" | "private" | string;
  range?: string;
  startDate?: string;
  endDate?: string;
  cmode?: string;
  language?: string;
  yoe?: string;
  serviceId?: string;
  specialty?: string;
  isEnabled?: boolean;
}

const queryFn = ({
  pagination,
  search,
  type,
  visibility,
  range,
  startDate,
  endDate,
  cmode,
  language,
  yoe,
  serviceId,
  specialty,
}: QueryProps) => {
  return api.get("profile/", {
    params: {
      ...(pagination && {
        page: pagination.page,
        page_size: pagination.pageSize,
      }),
      ...(search && { search }),
      ...(type && { type }),
      ...(visibility && { visibility }),
      ...(range && { range }),
      ...(startDate && { start_date: startDate }),
      ...(endDate && { end_date: endDate }),
      ...(cmode && { cmode }),
      ...(language && { language }),
      ...(yoe && { yoe }),
      ...(serviceId && { service_id: serviceId }),
      ...(specialty && { specialty }),
    },
  });
};

const select = (res: any) => {
  // console.log(res.data);

  // Check if response has the expected structure
  if (res.data && res.data.data && Array.isArray(res.data.data)) {
    const pagination: Pagination = {
      page: res.data.pagination.page,
      pageCount: res.data.pagination.page_count,
      pageSize: res.data.pagination.page_size,
      total: res.data.pagination.total,
    };

    const newData: Profile[] = res.data.data.map((profile: any) => ({
      id: profile._id,
      slug: profile.slug,
      profileImage: profile.profile_image,
      profileType: profile.profile_type,
      specialization: profile.specialization,
      primarySpecializationId: profile.primary_specialization_id,
      primarySpecialization: profile.primary_specialization,
      briefDescription: profile.brief_description,
      location: profile.location,
      yearsOfExperience: profile.years_of_experience,
      licenseNumber: profile.license_number,
      specialties: profile.specialties || [],
      services: profile.services || [],
      education: profile.education || [],
      languages: profile.languages || [],
      officeHours: profile.office_hours,
      socialMedia: profile.social_media || {},
      awardsAndPublications: profile.awards_and_publications || [],
      professionalMemberships: profile.professional_memberships || [],
      consultationModes: profile.consultation_modes || [],
      visibility: profile.visibility,
      partnerId: profile.partner_id,
      userId: profile.user_id,
      userInfo: profile.user_info ? {
        id: profile.user_info._id,
        fullName: profile.user_info.full_name,
        username: profile.user_info.username,
        email: profile.user_info.email,
        isActive: profile.user_info.is_active,
      } : profile.user_info,
      partnerInfo: profile.partner_info ? {
        id: profile.partner_info._id,
        name: profile.partner_info.name,
        enterpriseId: profile.partner_info.enterprise_id,
        createdAt: profile.partner_info.created_at,
        updatedAt: profile.partner_info.updated_at,
      } : profile.partner_info,
      createdAt: profile.created_at,
      updatedAt: profile.updated_at,
      archived: profile.archived || false,
    }));

    return {
      newData,
      pagination,
    };
  }

  // Fallback for empty or malformed response
  return {
    newData: [],
    pagination: {
      page: 1,
      pageCount: 1,
      pageSize: 25,
      total: 0,
    },
  };
};

export const useGetProfiles = ({
  pagination,
  search,
  type,
  visibility,
  range,
  startDate,
  endDate,
  cmode,
  language,
  yoe,
  serviceId,
  specialty,
  isEnabled = true,
}: QueryProps = {}) => {
  const handleErrors = useQueryErrorHandling();

  return useQuery({
    queryKey: [
      ...profileQKeys.allWithFilters({
        pagination,
        search,
        type,
        visibility,
        range,
        startDate,
        endDate,
        cmode,
        language,
        yoe,
        serviceId,
        specialty,
      }),
    ],
    queryFn: () =>
      queryFn({
        pagination,
        search,
        type,
        visibility,
        range,
        startDate,
        endDate,
        cmode,
        language,
        yoe,
        serviceId,
        specialty,
      }),
    select,
    staleTime: Infinity,
    enabled: isEnabled,

    onError: (res: any) => {
      console.error("[From Get Profiles]", res);
      handleErrors(res, "query");
    },
  });
};
